import { mqttLogger } from '@hooks/mqtt/utils';
import { atom } from 'jotai';
import { MqttClient } from 'mqtt';
import {
  ConnectionEvent,
  connectToMqtt,
  disconnectFromMqtt,
  getConnectionStatus,
  logConnectionEvent,
} from '../utils/connectionUtils';
import {
  type MqttConnectionAction,
  MqttConnectionActionType,
  MqttInitializationStatus,
  type MqttMetadata,
} from './types';

export const mqttClientAtom = atom<MqttClient | null>(null);
export const mqttMetadataAtom = atom<MqttMetadata>({});

export const mqttConnectionAtom = atom(
  (get) => {
    const client = get(mqttClientAtom);
    const metadata = get(mqttMetadataAtom);
    const status = getConnectionStatus(client, metadata);

    return {
      client,
      status,
      error: metadata.error,
      lastConnectedAt: metadata.lastConnectedAt,
      isConnected: client?.connected ?? false,
      isInitialized: !!client && (client.connected || client.reconnecting),
      isInitializing: metadata.isInitializing ?? false,
    };
  },
  async (get, set, action: MqttConnectionAction) => {
    const client = get(mqttClientAtom);

    switch (action.type) {
      case MqttConnectionActionType.ENSURE:
        if (client?.connected || client?.reconnecting) {
          return client;
        }
        return connectToMqtt(get, set, mqttClientAtom, mqttMetadataAtom);

      case MqttConnectionActionType.CONNECT:
        return connectToMqtt(get, set, mqttClientAtom, mqttMetadataAtom);

      case MqttConnectionActionType.DISCONNECT:
        return disconnectFromMqtt(
          get,
          set,
          mqttClientAtom,
          mqttMetadataAtom,
          action.force,
        );

      case MqttConnectionActionType.RECONNECT:
        if (!client) {
          mqttLogger.warn('No client available for reconnection');
          return;
        }

        if (client.connected) {
          mqttLogger.info('Client already connected');
          return;
        }

        if (client.reconnecting) {
          mqttLogger.info('Client already reconnecting');
          return;
        }

        client.reconnect();
        logConnectionEvent(ConnectionEvent.Reconnecting);
        return;

      case MqttConnectionActionType.GET_CLIENT:
        return client;

      default:
        return null;
    }
  },
);

export const mqttConnectionDetailsAtom = atom((get) => {
  const client = get(mqttClientAtom);
  const metadata = get(mqttMetadataAtom);

  return {
    status: getConnectionStatus(client, metadata),
    isConnected: client?.connected ?? false,
    error: metadata.error,
  };
});

export const mqttInitializationStatusAtom = atom<MqttInitializationStatus>(
  (get) => {
    const client = get(mqttClientAtom);
    const metadata = get(mqttMetadataAtom);

    if (metadata.error) {
      return MqttInitializationStatus.ERROR;
    }
    if (client?.connected) {
      return MqttInitializationStatus.INITIALIZED;
    }
    if (metadata.isInitializing) {
      return MqttInitializationStatus.INITIALIZING;
    }
    return MqttInitializationStatus.IDLE;
  },
);

mqttConnectionAtom.debugLabel = 'mqttConnectionAtom';
mqttClientAtom.debugLabel = 'mqttClientAtom';
mqttMetadataAtom.debugLabel = 'mqttMetadataAtom';
mqttConnectionDetailsAtom.debugLabel = 'mqttConnectionDetailsAtom';
mqttInitializationStatusAtom.debugLabel = 'mqttInitializationStatusAtom';
